import React, { useState } from "react";
import { Settings, BarChart3, Filter } from "lucide-react";
import SearchBar from "../../../components/SearchBar";
import ReusableDropdown from "../../../components/ReusableDropdown";
import { text } from "../constants/text";
import MobileFilterToggle from "../../../components/MobileFilterToggle";

export default function FiltersPanel({
  theme,
  language,
  styles,
  searchTerm,
  setSearchTerm,
  searchClientCode,
  setSearchClientCode,
  codeSearchField,
  setCodeSearchField,
  filterDepartment,
  setFilterDepartment,
  filterStatus,
  setFilterStatus,
  filterCompany,
  setFilterCompany,
  departmentsList = [],
  companiesList = [],
  screenSize,
  filteredCount,
  totalCount,
  actionButtons,
}) {
  const isRTL = language === "ar";

  // Phones: every field but the name search folds behind a "Filters" button.
  const [filtersOpen, setFiltersOpen] = useState(false);
  const activeFilterCount = [searchClientCode, filterDepartment, filterStatus, filterCompany].filter(Boolean).length;

  // ── Shared label style (same as Attendance) ──
  // minHeight keeps every label row the same height as the tallest one (the
  // identifier box's label carries a segmented toggle), so all four inputs
  // start on the same line.
  const labelStyle = {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    minHeight: "28px",
    marginBottom: "8px",
    fontSize: "14px",
    fontWeight: "600",
    flexDirection: "row",
    textAlign: isRTL ? "right" : "left",
  };

  // Each grid cell is a column whose input is pinned to the bottom, so a label
  // that wraps to two lines can never knock its own input out of line.
  const fieldStyle = {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  };

  const controlStyle = { marginTop: "auto" };

  // Segmented toggle for the identifier box (Client Code | National ID)
  const segmentStyle = (active) => ({
    display: "inline-flex",
    alignItems: "center",
    height: "26px",
    padding: "0 10px",
    fontSize: "12px",
    fontWeight: active ? 700 : 500,
    borderRadius: "6px",
    cursor: "pointer",
    whiteSpace: "nowrap",
    border: `1px solid ${active ? "#3b82f6" : theme === "dark" ? "rgba(255,255,255,0.18)" : "#d1d5db"}`,
    background: active ? "#3b82f6" : theme === "dark" ? "rgba(255,255,255,0.06)" : "#ffffff",
    color: active ? "#ffffff" : theme === "dark" ? "rgba(255,255,255,0.75)" : "#374151",
  });

  return (
    <div>
      {/* ── Header row (same as Attendance FiltersPanel) ───────────── */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
          flexWrap: "wrap",
          gap: "16px",
          flexDirection: isRTL ? "row" : "row",
        }}
      >
        <div>
          <h3
            style={{
              fontSize: "clamp(16px, 3vw, 20px)",
              fontWeight: "700",
              margin: 0,
              display: "flex",
              alignItems: "center",
              gap: "10px",
              flexDirection: isRTL ? "row" : "row",
            }}
          >
            <Settings size={20} />
            {text[language]?.filtersActions || "Filters & Actions"}
          </h3>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "14px",
            flexWrap: "wrap",
            flexDirection: isRTL ? "row-reverse" : "row",
          }}
        >
          {/* <div
            style={{
              fontSize: "14px",
              color: theme === "dark" ? "rgba(255,255,255,0.7)" : "#6b7280",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              flexDirection: isRTL ? "row" : "row",
            }}
          >
            <BarChart3 size={16} />
            {text[language]?.showing || "Showing"}{" "}
            {typeof filteredCount !== "undefined" ? filteredCount : ""}{" "}
            {text[language]?.of || "of"}{" "}
            {typeof totalCount !== "undefined" ? totalCount : ""}{" "}
            {text[language]?.records || "records"}
          </div> */}
          <MobileFilterToggle
            open={filtersOpen}
            onToggle={() => setFiltersOpen((o) => !o)}
            activeCount={activeFilterCount}
            language={language}
            theme={theme}
          />
        </div>
      </div>

      {/* ── Filter inputs grid (same as Attendance) ──────────────────
          `nx-filter-grid--five` (styles/responsive.css) pins all five fields
          to ONE row on desktop; the inline auto-fit below is the fallback for
          tablets, where five 250px columns genuinely don't fit. */}
      <div
        className="nx-filter-grid nx-filter-grid--five"
        data-open={String(filtersOpen)}
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
          gap: "20px",
        }}
      >
        {/* Search — uses shared SearchBar component (same as Attendance) */}
        <div style={fieldStyle}>
          <label style={labelStyle}>
            {text[language]?.searchEmployee || "Search Employee"}
          </label>
          <div style={controlStyle}>
          <SearchBar
            value={searchTerm}
            onChange={(value) => setSearchTerm(value)}
            placeholder={text[language]?.searchNameIdEmail || "Name, insurance no., email, phone..."}
            theme={theme}
            language={language}
            icon={true}
          />
          </div>
        </div>

        {/* Identifier box — searches ONE of Client Code / National ID. Both at
            once is useless: a short client code is a substring of plenty of
            14-digit national IDs, which buries the row you actually want. */}
        <div className="nx-filter-extra" style={fieldStyle}>
          <div
            style={{
              ...labelStyle,
              justifyContent: "space-between",
              gap: "8px",
              flexWrap: "wrap",
            }}
          >
            <span>{text[language]?.searchBy || "Search by"}</span>
            <div style={{ display: "flex", gap: "4px" }}>
              {[
                { key: "clientCode", label: text[language]?.clientCode || "Client Code" },
                { key: "nationalId", label: text[language]?.nationalId || "National ID" },
              ].map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setCodeSearchField(opt.key)}
                  style={segmentStyle(codeSearchField === opt.key)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div style={controlStyle}>
          <SearchBar
            value={searchClientCode}
            onChange={(value) => setSearchClientCode(value)}
            placeholder={
              codeSearchField === "nationalId"
                ? text[language]?.searchNationalId || "Search national ID..."
                : text[language]?.searchClientCode || "Search client code..."
            }
            theme={theme}
            language={language}
            icon={true}
          />
          </div>
        </div>

        {/* Department — uses ReusableDropdown (same as Attendance StatusDropdown) */}
        <div className="nx-filter-extra" style={fieldStyle}>
          <label style={labelStyle}>
            <Filter size={16} />
            {text[language]?.department || "Department"}
          </label>
          <div style={{ ...controlStyle, position: "relative" }}>
            <ReusableDropdown
              items={[
                { key: "", label: text[language]?.allDepartments || "All" },
                ...departmentsList.map((dep) => ({ key: dep, label: dep })),
              ]}
              value={filterDepartment}
              onChange={(val) => setFilterDepartment(val)}
              onSelect={(item) => setFilterDepartment(item.key)}
              onClear={() => setFilterDepartment("")}
              placeholder={text[language]?.searchDepartment || "Search department..."}
              getItemKey={(it) => it.key}
              getItemLabel={(it) => it.label}
              theme={theme}
              language={language}
              maxItems={8}
              showAllOnArrowClick={true}
            />
          </div>
        </div>

        {/* Status — uses ReusableDropdown (same as Attendance StatusDropdown) */}
        <div className="nx-filter-extra" style={fieldStyle}>
          <label style={labelStyle}>
            <Filter size={16} />
            {text[language]?.Status || "Status"}
          </label>
          <div style={{ ...controlStyle, position: "relative" }}>
            <ReusableDropdown
              items={[
                { key: "", label: text[language]?.allStatuses || "All" },
                { key: "Active", label: text[language]?.statusActive || "Active" },
                { key: "OnLeave", label: text[language]?.statusOnLeave || "On Leave" },
                { key: "Inactive", label: text[language]?.statusInactive || "Inactive" },
              ]}
              value={filterStatus}
              onChange={(val) => setFilterStatus(val)}
              onSelect={(item) => setFilterStatus(item.key)}
              onClear={() => setFilterStatus("")}
              placeholder={text[language]?.selectStatus || "Select"}
              getItemKey={(it) => it.key}
              getItemLabel={(it) => it.label}
              theme={theme}
              language={language}
              maxItems={6}
              showAllOnArrowClick={true}
            />
          </div>
        </div>

        {/* Company — uses ReusableDropdown (same as Attendance StatusDropdown) */}
        <div className="nx-filter-extra" style={fieldStyle}>
          <label style={labelStyle}>
            <Filter size={16} />
            {text[language]?.company || "Company"}
          </label>
          <div style={{ ...controlStyle, position: "relative" }}>
            <ReusableDropdown
              items={[
                { key: "", label: text[language]?.allCompanies || "All" },
                ...companiesList.map((c) => ({ key: c, label: c })),
              ]}
              value={filterCompany}
              onChange={(val) => setFilterCompany(val)}
              onSelect={(item) => setFilterCompany(item.key)}
              onClear={() => setFilterCompany("")}
              placeholder={text[language]?.allCompanies || "All"}
              getItemKey={(it) => it.key}
              getItemLabel={(it) => it.label}
              theme={theme}
              language={language}
              maxItems={8}
              showAllOnArrowClick={true}
            />
          </div>
        </div>
      </div>

      {/* ── Action Buttons ─────────────────────────────────────────────── */}
      {actionButtons && (
        <div style={{ marginTop: "20px" }}>
          {actionButtons}
        </div>
      )}
    </div>
  );
}